/**
 * "Incoming IncomingShipments" page - view incoming POs and Transshipments
 */

import React, { useRef, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import PageLayout from "../components/app/page-layout";
import IncomingShipmentsTable from "../components/incoming-shipments/incoming-shipments-table";
// import TableFooter from "../components/app/table-footer";
import IncomingShipmentsEmpty from "../components/incoming-shipments/incoming-shipments-empty";
import IncomingShipmentsActionFilterBar from "../components/incoming-shipments/incoming-shipments-filter-bar";
import FilterTags from "../components/incoming-shipments/filter-tags";
import QuantityDisplayBanner from "../components/app/quantity-display-banner";
import usePagination from "../hooks/use-pagination";
import useIncomingShipmentsFiltersNew from "../hooks/use-incoming-shipments-filters-new";
import usePageLayoutContentRectangle from "../hooks/use-page-layout-content-rectangle";
import filterIncomingShipments from "../filters/filter-incoming-shipments";
import useWarehouseData from "../hooks/use-warehouse-data";
import IncomingShipmentsScheduledActionBar from "../components/incoming-shipments/incoming-shipments-scheduled-action-bar";
import Divider from "@amzn/meridian/divider";
import IncomingShipmentsUnscheduledTable from "../components/incoming-shipments/incoming-shipments-unscheduled-table";

import { convertUnix } from "../utilities/convert-unix";
import { getWarehouseAttributes } from "../utilities/get-warehouse-attributes";
// import { resultsPerPageOptions } from "../components/Constants";
import { getSummaryQuantity } from "../utilities/parent-child-conversion";
// import useIncomingShipmentsViewPreferences from "../hooks/use-incoming-shipments-view-preferences";
import IncomingShipmentsScipForecastsTable from "../components/incoming-shipments/incoming-shipments-scip-forecasts-table";
import IncomingShipmentsScipActionBar from "../components/incoming-shipments/incoming-shipments-scip-action-bar";

const getTableWidth = (wrapperElement) => {
  try {
    return wrapperElement.querySelector("table").clientWidth;
  } catch (_) {
    return undefined;
  }
};

const IncomingShipmentsNewView = (props) => {
  const {
    warehouseId,
    incomingIncomingShipments,
    incomingUnscheduledShipments,
    incomingScipForecastsDetailed,
    manifestedVrids,
    lastRefreshTime,
    parsedUnscheduledIncomingShipmentsLoadId,
    parsedIncomingShipmentsLoadId,
  } = props;
  const { filters } = useIncomingShipmentsFiltersNew();
  const { width: contentWidth = 1200 } = usePageLayoutContentRectangle();
  const tableWrapperRef = useRef();
  const [resultsPerPage, setResultsPerPage] = useState("All");
  const [selectedResultsPerPage, setSelectedResultsPerPage] = useState("All");

  const { time_zone, region_id } = getWarehouseAttributes({
    warehouseId: warehouseId,
    warehouseParams: useWarehouseData(),
  });

  const shipments =
    incomingIncomingShipments &&
    filterIncomingShipments(incomingIncomingShipments, filters);

  const shipments2 =
    incomingUnscheduledShipments &&
    filterIncomingShipments(incomingUnscheduledShipments, filters, "unscheduled", region_id);

  const shipmentsLoadId =
    parsedUnscheduledIncomingShipmentsLoadId &&
    filterIncomingShipments(parsedUnscheduledIncomingShipmentsLoadId, filters, "unscheduled", region_id);

  const shipmentsScheduledLoadId =
    parsedIncomingShipmentsLoadId &&
    filterIncomingShipments(parsedIncomingShipmentsLoadId, filters);

  // Since we now only have detailed rows, we don't need to filter incomingScipForecasts separately
  const scipForecastsDetailed =
    incomingScipForecastsDetailed &&
    filters?.get("dateRangeNew")?.value &&
    filterIncomingShipments(incomingScipForecastsDetailed, filters, "scip");



  // Memoize totals calculation
  const { totalIncomingUnits, totalIncomingPallets } = useMemo(() => {
    if (!shipments) {
      return { totalIncomingUnits: 0, totalIncomingPallets: 0 };
    }

    return shipments.reduce(
      (acc, shipment) => ({
        totalIncomingUnits:
          acc.totalIncomingUnits + (getSummaryQuantity(shipment, 'expectedUnits', 'childExpectedUnits') || 0),
        totalIncomingPallets:
          acc.totalIncomingPallets + (shipment.expectedPallets || 0),
      }),
      { totalIncomingUnits: 0, totalIncomingPallets: 0 }
    );
  }, [shipments]);

  const pagination = usePagination({
    numberOfItemsPerPage: resultsPerPage,
    numberOfResults: shipments?.length || 0,
  });
  const tableWidth = getTableWidth(tableWrapperRef.current);
  const isSticky = contentWidth < tableWidth;

  const { visibleIndices } = pagination;

  const onChangeResultsPerPage = (resultsPerPage) => {
    // Update local state variable
    setSelectedResultsPerPage(resultsPerPage);
    if (resultsPerPage === "All") {
      setResultsPerPage(shipments?.length || 0);
    } else {
      setResultsPerPage(resultsPerPage);
    }
    // Change pagination back to page 1. This avoids the weird state users can
    // find themselves in where they're on a page that no longer exists if the
    // number of pages decreases.
    pagination.onChange(1);
  };

  // Set pagination back to page 1 when filters change
  useEffect(() => {
    // Change pagination back to page 1. This avoids the weird state users can
    // find themselves in where they're on a page that no longer exists if the
    // number of pages decreases.
    onChangeResultsPerPage(selectedResultsPerPage);
    // We disable the exhaustive-deps hooks rule here because including `pagination`
    // will cause the effect to be called over-and-over.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filters, incomingIncomingShipments]);

  return (
    <PageLayout
      loading={!shipments}
      width={shipments ? "inherit" : undefined}
      horizontallySticky={isSticky}
      maxWidth={isSticky ? "none" : undefined}
      alignmentHorizontal={isSticky ? "start" : "center"}
    >
      <Column spacing="300">
        <QuantityDisplayBanner region_id={region_id} />
        <Text type={"b100"}>
          Last Refresh: {convertUnix(lastRefreshTime, time_zone)}
        </Text>
        <IncomingShipmentsActionFilterBar
          stick={isSticky}
          incomingShipments={shipments}
          totalIncomingUnits={totalIncomingUnits}
          totalIncomingPallets={totalIncomingPallets}
          warehouseId={warehouseId}
          isNewView={true}
        />

        <FilterTags stick={isSticky} isNewView={true} />

        {filters?.size > 0 &&
          !(
            filters?.get("dateRangeNew")?.value?.length === 1 &&
            filters?.get("dateRangeNew")?.value?.[0] === "yesterday"
          ) ? (
          <>
            <IncomingShipmentsScipActionBar
              stick={isSticky}
              forecasts={scipForecastsDetailed}
              warehouseId={warehouseId}
            />

            <IncomingShipmentsScipForecastsTable
              scipForecastsDetailed={scipForecastsDetailed}
              manifestedVrids={manifestedVrids}
              warehouseId={warehouseId}
            />
          </>
        ) : null}

        <Divider size="large" />

        <IncomingShipmentsScheduledActionBar
          // stick={isSticky}
          incomingShipments={shipments}
          table={"scheduled"}
        />
        <Row
          spacingInset={isSticky ? "none 500 none none" : undefined}
          ref={tableWrapperRef}
        >
          <IncomingShipmentsTable
            incomingShipments={shipments}
            shipmentsScheduledLoadId={shipmentsScheduledLoadId}
            visibleIndices={visibleIndices}
            warehouseId={warehouseId}
          />
        </Row>

        {shipments && shipments.length === 0 ? (
          <IncomingShipmentsEmpty searching={Boolean(filters?.size)} />
        ) : null}
      </Column>

      <Divider size="large" />

      <IncomingShipmentsScheduledActionBar
        // stick={isSticky}
        incomingShipments={shipments2}
        table={"pending"}
      />
      <Row
        spacingInset={isSticky ? "none 500 none none" : undefined}
        ref={tableWrapperRef}
      >
        <IncomingShipmentsUnscheduledTable
          incomingShipments={shipments2}
          shipmentsLoadId={shipmentsLoadId}
          visibleIndices={visibleIndices}
          warehouseId={warehouseId}
        />
      </Row>

      {shipments2 && shipments2.length === 0 ? (
        <IncomingShipmentsEmpty searching={Boolean(filters?.size)} />
      ) : null}
    </PageLayout>
  );
};

IncomingShipmentsNewView.propTypes = {
  parsedUpdated: PropTypes.object,
  incomingIncomingShipments: PropTypes.arrayOf(PropTypes.object),
  lastRefreshTime: PropTypes.number.isRequired,
  warehouseId: PropTypes.string.isRequired,
  manifestedVrids: PropTypes.arrayOf(PropTypes.object),
};

export default IncomingShipmentsNewView;
